import { useEffect } from "react";

/** ---------- Reusable Legal Modal ---------- */
export type LegalDoc = "privacy" | "terms";

export interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  doc: LegalDoc | null;
}

export const LegalModal: React.FC<LegalModalProps> = ({
  isOpen,
  onClose,
  doc,
}) => {
  if (!isOpen || !doc) return null;

  const title = doc === "privacy" ? "Privacy Policy" : "Terms & Conditions";
  const src = doc === "privacy" ? "/privacy" : "/terms";

  // Close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      onMouseDown={onClose} // overlay click to close
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        className="relative mx-auto w-[92vw] max-w-5xl h-[82vh] rounded-lg bg-white shadow-2xl border border-gray-200 flex flex-col"
        onMouseDown={(e) => e.stopPropagation()} // prevent closing when clicking inside dialog
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b">
          <h2
            id="legal-modal-title"
            className="text-xl font-semibold text-gray-900"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            aria-label="Close"
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div className="flex-1">
          <iframe
            title={title}
            src={src}
            className="w-full h-full rounded-b-lg"
          />
        </div>
      </div>
    </div>
  );
};
/** ---------- End: Legal Modal ---------- */
